import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, InputGroup,
  DropdownButton, Dropdown, Accordion
} from 'react-bootstrap';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL } from '../../services/api';

const CoursesManager = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  
  // Options
  const [availableIcons, setAvailableIcons] = useState({});
  const [courseTypes, setCourseTypes] = useState({});
  const [statusOptions, setStatusOptions] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    duration: '',
    eligibility: '',
    fee: '',
    intake_seats: 60,
    order_display: 0,
    status: 'active',
    syllabus: '',
    course_type: 'undergraduate',
    icon: 'fa-graduation-cap'
  });

  const API_BASE_URL = BASE_URL;
  
  useEffect(() => {
    // Fetch user data
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    
    fetchCourses();
    fetchOptions();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      duration: '',
      eligibility: '',
      fee: '',
      intake_seats: 60,
      order_display: 0,
      status: 'active',
      syllabus: '',
      course_type: 'undergraduate',
      icon: 'fa-graduation-cap'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    window.location.href = '/admin/login';
  };

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/courses.php?admin=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const processedCourses = response.data.data.map(course => ({
          ...course,
          id: parseInt(course.id),
          order_display: parseInt(course.order_display) || 0,
          fee: parseFloat(course.fee) || 0,
          intake_seats: parseInt(course.intake_seats) || 0
        })).sort((a, b) => a.order_display - b.order_display);
        
        setCourses(processedCourses);
      } else {
        setError('Failed to load courses');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      setOptionsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE_URL}/courses.php?options=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setAvailableIcons(response.data.data.icons || {});
        setCourseTypes(response.data.data.course_types || {});
        setStatusOptions(response.data.data.status_options || {});
      }
    } catch (err) {
      console.error('Error fetching options:', err);
    } finally {
      setOptionsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconSelect = (icon) => {
    setFormData(prev => ({
      ...prev,
      icon: icon
    }));
  };

  const handleCourseTypeSelect = (course_type) => {
    setFormData(prev => ({
      ...prev,
      course_type: course_type
    }));
  };

  const handleStatusSelect = (status) => {
    setFormData(prev => ({
      ...prev,
      status: status
    }));
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getSeatStatusBadge = (seats) => {
    if (seats === null || seats === undefined || seats === 0) {
      return <Badge bg="secondary">No Seats</Badge>;
    } else if (seats > 30) {
      return <Badge bg="success">{seats} Seats</Badge>;
    } else if (seats > 10) {
      return <Badge bg="warning">{seats} Seats</Badge>;
    } else {
      return <Badge bg="danger">{seats} Seats</Badge>;
    }
  };

  const getCourseTypeBadge = (type) => {
    const typeColors = {
      'undergraduate': 'primary',
      'postgraduate': 'info',
      'diploma': 'success',
      'certificate': 'warning',
      'phd': 'danger',
      'vocational': 'secondary'
    };
    
    const label = courseTypes[type] || type;
    const color = typeColors[type] || 'secondary';
    
    return <Badge bg={color}>{label}</Badge>;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'inactive':
        return <Badge bg="secondary">Inactive</Badge>;
      case 'upcoming':
        return <Badge bg="warning">Upcoming</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data
      const dataToSend = { 
        ...formData,
        fee: formData.fee ? parseFloat(formData.fee) : 0,
        order_display: parseInt(formData.order_display) || 0,
        intake_seats: parseInt(formData.intake_seats) || 0
      };
      
      const token = localStorage.getItem('token');
      let response;
      
      if (editingCourse) {
        response = await axios.put(
          `${API_BASE_URL}/courses.php?id=${editingCourse.id}`,
          dataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/courses.php`, dataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
      
      if (response.data.success) {
        setSuccess(editingCourse ? 'Course updated!' : 'Course added!');
        setShowModal(false);
        resetForm();
        setEditingCourse(null);
        fetchCourses();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      setError('Error saving course: ' + (err.response?.data?.message || err.message));
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_BASE_URL}/courses.php?id=${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setSuccess('Course deleted!');
          fetchCourses();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete course: ' + (response.data.message || ''));
        }
      } catch (err) {
        setError('Error deleting course');
        console.error(err);
      }
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name || '',
      code: course.code || '',
      description: course.description || '',
      duration: course.duration || '',
      eligibility: course.eligibility || '',
      fee: course.fee || '',
      intake_seats: course.intake_seats || 60,
      order_display: course.order_display || 0,
      status: course.status || 'active',
      syllabus: course.syllabus || '',
      course_type: course.course_type || 'undergraduate',
      icon: course.icon || 'fa-graduation-cap'
    });
    
    setShowModal(true);
  };

  const handleToggleStatus = async (course) => {
    try {
      const newStatus = course.status === 'active' ? 'inactive' : 'active';
      
      const updatedData = {
        name: course.name,
        code: course.code,
        description: course.description,
        duration: course.duration,
        eligibility: course.eligibility,
        fee: course.fee,
        intake_seats: course.intake_seats,
        order_display: course.order_display,
        status: newStatus,
        syllabus: course.syllabus,
        course_type: course.course_type,
        icon: course.icon
      };
      
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/courses.php?id=${course.id}`,
        updatedData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.data.success) {
        fetchCourses();
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to update status');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCourse(null);
    resetForm();
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(courses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      order_display: index + 1
    }));
    
    setCourses(updatedItems);
    
    // Send update to server
    try {
      const token = localStorage.getItem('token');
      const reorderData = updatedItems.map(item => ({
        id: item.id,
        order_display: item.order_display
      }));
      
      const response = await axios.post(`${API_BASE_URL}/courses.php`, {
        reorder: reorderData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.data.success) {
        setError('Failed to save order changes');
        fetchCourses(); // Revert to server state
      }
    } catch (err) {
      console.error('Error reordering:', err);
      setError('Failed to save order changes');
      fetchCourses(); // Revert to server state
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar onLogout={handleLogout} user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading courses...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar onLogout={handleLogout} user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Courses Manager</h2>
            <p className="text-muted">Manage college courses with drag & drop ordering</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => {
              const nextOrder = courses.length > 0 
                ? Math.max(...courses.map(course => parseInt(course.order_display))) + 1 
                : 1;
              setFormData(prev => ({ 
                ...prev, 
                order_display: nextOrder,
                status: 'active'
              }));
              setShowModal(true);
            }}>
              <i className="fas fa-plus me-2"></i>Add Course
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="courses">
                {(provided) => (
                  <Table responsive hover ref={provided.innerRef} {...provided.droppableProps}>
                    <thead>
                      <tr>
                        <th width="60">Drag</th>
                        <th width="80">Icon</th>
                        <th>Course Name</th>
                        <th>Code</th>
                        <th>Duration</th>
                        <th>Fee</th>
                        <th>Seats</th>
                        <th>Type</th>
                        <th>Order</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th width="150">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map((course, index) => (
                        <Draggable key={course.id} draggableId={course.id.toString()} index={index}>
                          {(provided) => (
                            <tr 
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <td {...provided.dragHandleProps}>
                                <i className="fas fa-grip-vertical text-muted"></i>
                              </td>
                              <td>
                                <div className="text-primary fs-4">
                                  <i className={course.icon || 'fa-graduation-cap'}></i>
                                </div>
                              </td>
                              <td>
                                <strong>{course.name}</strong>
                                <div className="small text-muted">
                                  {truncateText(course.description, 50)}
                                </div>
                              </td>
                              <td>
                                <code>{course.code}</code>
                              </td>
                              <td>{course.duration}</td>
                              <td>
                                <strong>{formatCurrency(course.fee)}</strong>
                              </td>
                              <td>
                                {getSeatStatusBadge(course.intake_seats)}
                              </td>
                              <td>{getCourseTypeBadge(course.course_type)}</td>
                              <td>
                                <Badge bg="secondary">{course.order_display}</Badge>
                              </td>
                              <td>{getStatusBadge(course.status)}</td>
                              <td>
                                <small className="text-muted">
                                  {formatDate(course.created_at)}
                                </small>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEdit(course)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(course.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </Table>
                )}
              </Droppable>
            </DragDropContext>
            
            {courses.length === 0 && (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-book fa-3x mb-3"></i>
                <p>No courses found. Add your first course!</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Add/Edit Course Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingCourse ? 'Edit Course' : 'Add Course'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Course Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Bachelor of Computer Science"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Course Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder="e.g., BSC-CS"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={formData.duration}
                      onChange={handleInputChange}
                      placeholder="e.g., 4 Years"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fee (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      name="fee"
                      value={formData.fee}
                      onChange={handleInputChange}
                      placeholder="e.g., 120000"
                      min="0"
                      step="1000"
                    />
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Intake Seats</Form.Label>
                    <Form.Control
                      type="number"
                      name="intake_seats"
                      value={formData.intake_seats}
                      onChange={handleInputChange}
                      placeholder="e.g., 60"
                      min="0"
                      step="1"
                    />
                    <Form.Text className="text-muted">
                      Total available seats for this course
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      name="order_display"
                      value={formData.order_display}
                      onChange={handleInputChange}
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Course Type</Form.Label>
                    <DropdownButton
                      variant="outline-secondary"
                      title={courseTypes[formData.course_type] || 'Select Type'}
                      onSelect={handleCourseTypeSelect}
                      block
                    >
                      {Object.entries(courseTypes).map(([value, label]) => (
                        <Dropdown.Item key={value} eventKey={value}>
                          {label}
                        </Dropdown.Item>
                      ))}
                    </DropdownButton>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <div>
                      <DropdownButton
                        variant="outline-secondary"
                        title={statusOptions[formData.status] || 'Select Status'}
                        onSelect={handleStatusSelect}
                        block
                      >
                        {Object.entries(statusOptions).map(([value, label]) => (
                          <Dropdown.Item key={value} eventKey={value}>
                            {label}
                          </Dropdown.Item>
                        ))}
                      </DropdownButton>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleTextareaChange}
                  rows={3}
                  placeholder="Course description..."
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Eligibility Criteria</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="eligibility"
                      value={formData.eligibility}
                      onChange={handleTextareaChange}
                      rows={4}
                      placeholder="Eligibility requirements..."
                    />
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Syllabus</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="syllabus"
                      value={formData.syllabus}
                      onChange={handleTextareaChange}
                      rows={4}
                      placeholder="Course syllabus/syllabus overview..."
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Icon</Form.Label>
                    <div className="d-flex align-items-center">
                      {formData.icon && (
                        <div className="text-primary fs-4 me-3">
                          <i className={formData.icon}></i>
                        </div>
                      )}
                      <DropdownButton
                        variant="outline-secondary"
                        title={formData.icon ? availableIcons[formData.icon] || 'Change Icon' : 'Select Icon'}
                        onSelect={handleIconSelect}
                      >
                        {Object.entries(availableIcons).map(([icon, label]) => (
                          <Dropdown.Item key={icon} eventKey={icon}>
                            <i className={`fas ${icon} me-2`}></i>
                            {label}
                          </Dropdown.Item>
                        ))}
                      </DropdownButton>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              
              {/* Preview Section */}
              <Accordion className="mt-3">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Course Preview</Accordion.Header>
                  <Accordion.Body>
                    <Card>
                      <Card.Body>
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <div className="text-primary fs-1">
                              <i className={formData.icon || 'fa-graduation-cap'}></i>
                            </div>
                          </div>
                          <div>
                            <h5 className="mb-1">{formData.name || 'Course Name'}</h5>
                            <div className="mb-2">
                              <code className="me-3">{formData.code || 'CODE'}</code>
                              <Badge bg="info" className="me-2">{formData.duration || 'Duration'}</Badge>
                              {getCourseTypeBadge(formData.course_type)}
                            </div>
                            <div className="mb-2">
                              <div className="d-flex align-items-center">
                                <strong className="text-success me-3">{formatCurrency(formData.fee)}</strong>
                                {getSeatStatusBadge(formData.intake_seats)}
                              </div>
                            </div>
                            {formData.description && (
                              <p className="text-muted small mb-2">
                                {truncateText(formData.description, 200)}
                              </p>
                            )}
                            <div className="d-flex flex-wrap gap-2">
                              <Badge bg={formData.status === 'active' ? 'success' : 
                                       formData.status === 'upcoming' ? 'warning' : 'secondary'}>
                                {statusOptions[formData.status] || formData.status}
                              </Badge>
                              <Badge bg="secondary">Order: {formData.order_display}</Badge>
                            </div>
                          </div>
                        </div>
                        
                        {(formData.eligibility || formData.syllabus) && (
                          <Row className="mt-3">
                            {formData.eligibility && (
                              <Col md={6}>
                                <h6>Eligibility:</h6>
                                <p className="small text-muted">{truncateText(formData.eligibility, 100)}</p>
                              </Col>
                            )}
                            {formData.syllabus && (
                              <Col md={6}>
                                <h6>Syllabus:</h6>
                                <p className="small text-muted">{truncateText(formData.syllabus, 100)}</p>
                              </Col>
                            )}
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              
              {editingCourse && (
                <div className="alert alert-secondary mt-3">
                  <small>
                    <div className="row">
                      <div className="col-md-6">
                        <i className="fas fa-calendar-plus me-2"></i>
                        Created: {formatDate(editingCourse.created_at)}
                      </div>
                      <div className="col-md-6">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated: {formatDate(editingCourse.updated_at)}
                      </div>
                    </div>
                  </small>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingCourse ? 'Update' : 'Add'} Course
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default CoursesManager;